import { Card } from "azure-devops-ui/Card";
import { IObservableValue, ObservableValue } from "azure-devops-ui/Core/Observable";
import { ScreenSize } from "azure-devops-ui/Core/Util/Screen";
import { Header, TitleSize } from "azure-devops-ui/Header";
import { IListItemDetails, List, ListItem, ListSelection } from "azure-devops-ui/List";
import { DetailsPanel, MasterPanel, MasterPanelHeader } from "azure-devops-ui/MasterDetails";
import {
    BaseMasterDetailsContext,
    bindSelectionToObservable,
    createChildLayer,
    IDetailsAreaContent,
    IMasterDetailsContext,
    IMasterDetailsContextLayer,
    IMasterPanelContent,
    MasterDetailsContext,
} from "azure-devops-ui/MasterDetailsContext";
import { Page } from "azure-devops-ui/Page";
import {
    ITableColumn,
    ITableRow,
    SimpleTableCell,
    Table,
    TwoLineTableCell,
} from "azure-devops-ui/Table";
import { TextField, TextFieldStyle } from "azure-devops-ui/TextField";
import { Tooltip } from "azure-devops-ui/TooltipEx";
import { VssPersona } from "azure-devops-ui/VssPersona";
import { ArrayItemProvider } from "azure-devops-ui/Utilities/Provider";
import { ScreenSizeObserver } from "azure-devops-ui/Utilities/ScreenSize";
import * as React from "react";
import "./MasterDetail.Example.css";

interface ISamplePullRequestData {
    commits: ICommitDetails[];
    pullRequestTitle: string;
    userImageUrl: string;
    userName: string;
}

interface ICommitDetails {
    date: string;
    description: string;
    hash: string;
}

const SampleData: ISamplePullRequestData[] = [
    {
        pullRequestTitle: "Added GitHub aliases",
        userImageUrl: require("./persona-male.png"),
        userName: "Cecil Folk",
        commits: [
            { description: "Finalized GitHub aliases", hash: "abc123", date: "2019-03-05" },
            { description: "Added Configuration", hash: "def456", date: "2019-03-04" },
            { description: "Updated CSS", hash: "ghi789", date: "2019-03-03" },
            { description: "Constructed pylons", hash: "123abc", date: "2019-03-02" },
        ],
    },
    {
        pullRequestTitle: "Remove reference to Design System components",
        userImageUrl: require("./persona-male.png"),
        userName: "Cecil Folk",
        commits: [
            { description: "Finalized GitHub aliases", hash: "abc123", date: "2019-03-05" },
            { description: "Added Configuration", hash: "def456", date: "2019-03-04" },
        ],
    },
    {
        pullRequestTitle: "Using new design/pattern/components",
        userImageUrl: require("./persona-female.png"),
        userName: "Ashley McCarthy",
        commits: [
            { description: "Updated CSS", hash: "ghi789", date: "2019-03-03" },
            { description: "Constructed pylons", hash: "123abc", date: "2019-03-02" },
        ],
    },
];

const renderInitialRow = (
    index: number,
    item: ISamplePullRequestData,
    details: IListItemDetails<ISamplePullRequestData>,
    key?: string
): JSX.Element => {
    return (
        <ListItem
            className="master-example-row"
            key={key || "list-item" + index}
            index={index}
            details={details}
        >
            <div className="master-example-row-content flex-row flex-center h-scroll-hidden">
                <VssPersona className="icon-margin" imageUrl={item.userImageUrl} />
                <div className="flex-column text-ellipsis">
                    <Tooltip overflowOnly={true}>
                        <div className="primary-text text-ellipsis">{item.pullRequestTitle}</div>
                    </Tooltip>
                    <Tooltip overflowOnly={true}>
                        <div className="primary-text text-ellipsis">{item.userName}</div>
                    </Tooltip>
                </div>
            </div>
        </ListItem>
    );
};

const initialPayload: IMasterDetailsContextLayer<ISamplePullRequestData, undefined> = {
    key: "initial",
    masterPanelContent: {
        renderContent: (parentItem, initialSelectedMasterItem) => (
            <InitialMasterPanelContent initialSelectedMasterItem={initialSelectedMasterItem} />
        ),
        renderHeader: () => <MasterPanelHeader title={"Engineering"} />,
        renderSearch: () => (
            <TextField
                prefixIconProps={{ iconName: "Search" }}
                placeholder="Search does not work"
                style={TextFieldStyle.inline}
            />
        ),
        hideBackButton: true,
    },
    detailsContent: {
        renderContent: (item) => <InitialDetailView detailItem={item} />,
    },
    selectedMasterItem: new ObservableValue<ISamplePullRequestData>(SampleData[0]),
    parentItem: undefined,
};

const InitialMasterPanelContent: React.FunctionComponent<{
    initialSelectedMasterItem: IObservableValue<ISamplePullRequestData>;
}> = (props) => {
    const [initialItemProvider] = React.useState(new ArrayItemProvider(SampleData));
    const [initialSelection] = React.useState(new ListSelection({ selectOnFocus: false }));
    const masterDetailsContext = React.useContext(MasterDetailsContext);

    React.useEffect(() => {
        bindSelectionToObservable(
            initialSelection,
            initialItemProvider,
            props.initialSelectedMasterItem
        );
    });

    return (
        <List
            ariaLabel={"Engineering master list"}
            itemProvider={initialItemProvider}
            selection={initialSelection}
            renderRow={renderInitialRow}
            width="100%"
            onSelect={() => masterDetailsContext.setDetailsPanelVisbility(true)}
        />
    );
};

const InitialDetailView: React.FunctionComponent<{
    detailItem: ISamplePullRequestData;
}> = (props) => {
    const masterDetailsContext = React.useContext(MasterDetailsContext);
    const { detailItem } = props;

    const renderCommitNameCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICommitDetails>,
        tableItem: ICommitDetails
    ) => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                className="fontWeightSemiBold fontSizeM scroll-hidden wrap-text"
                key={"col-" + columnIndex}
                tableColumn={tableColumn}
                line1={
                    <Tooltip text={tableItem.description}>
                        <span className="fontSizeM wrap-text">{tableItem.description}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip text={tableItem.hash}>
                        <span className="fontSize secondary-text flex-center wrap-text">{tableItem.hash}</span>
                    </Tooltip>
                }
            />
        );
    };

    const renderDateColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICommitDetails>,
        tableItem: ICommitDetails
    ) => {
        return (
            <SimpleTableCell
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <div>{tableItem.date}</div>
            </SimpleTableCell>
        );
    };

    const columns: Array<ITableColumn<ICommitDetails>> = [
        {
            id: "description",
            name: "Commit",
            width: new ObservableValue(-33),
            renderCell: renderCommitNameCell,
        },
        { id: "date", name: "Date", width: new ObservableValue(-33), renderCell: renderDateColumn },
    ];

    const onRowActivated = (event: any, tableRow: ITableRow<ICommitDetails>) => {
        const newPayload = createChildLayer(
            "commit-details",
            newMasterPanelContent,
            newDetailsContent,
            tableRow.data,
            initialPayload
        );
        masterDetailsContext.push(newPayload);
    };

    return (
        <Page className="context-details">
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    const showBackButton = screenSizeProps.screenSize <= ScreenSize.small;
                    return (
                        <Header
                            description={"Created by " + detailItem.userName}
                            descriptionClassName="description-primary-text"
                            title={detailItem.pullRequestTitle}
                            titleClassName="details-view-title"
                            titleSize={TitleSize.Large}
                            backButtonProps={
                                showBackButton
                                    ? {
                                          onClick: () =>
                                              masterDetailsContext.setDetailsPanelVisbility(false),
                                      }
                                    : undefined
                            }
                        />
                    );
                }}
            </ScreenSizeObserver>
            <div className="page-content page-content-top">
                <Card
                    className="bolt-card-no-vertical-padding"
                    contentProps={{ contentPadding: false }}
                >
                    <Table<ICommitDetails>
                        columns={columns}
                        itemProvider={new ArrayItemProvider<ICommitDetails>(detailItem.commits)}
                        showLines={true}
                        singleClickActivation={true}
                        onActivate={onRowActivated}
                    />
                </Card>
            </div>
        </Page>
    );
};

const renderNewRow = (
    index: number,
    item: ICommitDetails,
    details: IListItemDetails<ICommitDetails>,
    key?: string
): JSX.Element => {
    return (
        <ListItem
            className="master-example-row"
            key={key || "list-item" + index}
            index={index}
            details={details}
        >
            <div className="master-example-row-content flex-column h-scroll-hidden wrap-text">
                <Tooltip text={item.description}>
                    <div className="primary-text text-ellipsis">{item.description}</div>
                </Tooltip>
                <Tooltip text={item.hash}>
                    <div className="secondary-text text-ellipsis">{item.hash}</div>
                </Tooltip>
            </div>
        </ListItem>
    );
};

const newMasterPanelContent: IMasterPanelContent<ICommitDetails, ISamplePullRequestData> = {
    renderContent: (parentItem, initiallySelectedMasterItem) => {
        return (
            <NewMasterPanelContentComponent
                parentItem={parentItem}
                initiallySelectedMasterItem={initiallySelectedMasterItem}
            />
        );
    },
    renderHeader: (parentItem) => {
        return (
            <MasterPanelHeader
                title={parentItem.pullRequestTitle}
                subTitle={`Created by ${parentItem.userName}`}
            />
        );
    },
    hideBackButton: false,
};

const NewMasterPanelContentComponent: React.FunctionComponent<{
    parentItem: ISamplePullRequestData;
    initiallySelectedMasterItem: IObservableValue<ICommitDetails>;
}> = (props) => {
    const { parentItem, initiallySelectedMasterItem } = props;

    const [itemProvider] = React.useState(new ArrayItemProvider(parentItem.commits));
    const [selection] = React.useState(new ListSelection({ selectOnFocus: false }));
    const listRef = React.useRef<List<ICommitDetails>>();
    React.useEffect(() => {
        bindSelectionToObservable(selection, itemProvider, initiallySelectedMasterItem);
        listRef.current;
    }, []);

    return (
        <List<ICommitDetails>
            key="new-list"
            itemProvider={itemProvider}
            selection={selection}
            onSelect={() => masterDetailsContext.setDetailsPanelVisbility(true)}
            renderRow={renderNewRow}
            width="100%"
        />
    );
};

const newDetailsContent: IDetailsAreaContent<ICommitDetails> = {
    renderContent: (detailItem) => (
        <Page className="flex-grow context-details">
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    const showBackButton = screenSizeProps.screenSize <= ScreenSize.small;
                    return (
                        <Header
                            description={detailItem.hash}
                            descriptionClassName="description-primary-text"
                            title={detailItem.description}
                            titleSize={TitleSize.Large}
                            backButtonProps={
                                showBackButton
                                    ? {
                                          onClick: () =>
                                              masterDetailsContext.setDetailsPanelVisbility(false),
                                      }
                                    : undefined
                            }
                        />
                    );
                }}
            </ScreenSizeObserver>
            <div className="page-content page-content-top flex-column">
                <Card className="flex-grow">
                    <div className="flex-column">
                        <div className="title-xs">Created at</div>{" "}
                        <div className="body-m">{detailItem.date}</div>
                    </div>
                </Card>
            </div>
        </Page>
    ),
};

const masterDetailsContext: IMasterDetailsContext = new BaseMasterDetailsContext(
    initialPayload,
    () => {
        alert("Triggered onExit");
    }
);

const MasterDetailsContextExample: React.SFC<{}> = (props) => {
    return (
        <MasterDetailsContext.Provider value={masterDetailsContext}>
            <div className="flex-row">
                <MasterPanel className="master-example-panel" />
                <DetailsPanel />
            </div>
        </MasterDetailsContext.Provider>
    );
};

export default MasterDetailsContextExample;
